import { useMemo } from "react";

import { RecordingMomentRoles } from "@regal-voice/shared-types";
import { Tag } from "antd";
import { clsx } from "clsx";
import capitalize from "lodash/capitalize";
import isNil from "lodash/isNil";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { Column, Row } from "Components/shared/Flexbox";

import styles from "./SentimentDisplay.module.scss";

export type SentimentClassification = {
    identifier: "positive" | "neutral" | "negative";
    label: "Positive" | "Neutral" | "Negative";
};

export function classifySentiment(score: number): SentimentClassification {
    if (score >= 70) {
        return { identifier: "positive", label: "Positive" };
    }
    if (score >= 50) {
        return { identifier: "neutral", label: "Neutral" };
    }
    return { identifier: "negative", label: "Negative" };
}

export function SentimentDisplay({
    sentiments,
    role,
}: {
    sentiments?: Record<string, number | null>;
    role: RecordingMomentRoles;
}): JSX.Element {
    const sentimentType = role == "contact" ? "contactSentiment" : "agentSentiment";
    const sentiment = sentiments?.[sentimentType];

    const sentimentProps = useMemo(() => (isNil(sentiment) ? null : classifySentiment(sentiment)), [sentiment]);

    if (!sentimentProps) {
        return <>-</>;
    }

    return (
        <Tag
            key={`${role}-sentiment`}
            data-testid={`${role}-sentiment`}
            className={clsx(styles.tag, styles[sentimentProps.identifier])}
        >
            <SentimentContent sentimentLabel={sentimentProps.label} role={role} />
        </Tag>
    );
}

function SentimentContent({ sentimentLabel, role }: { sentimentLabel: string; role: string }): JSX.Element {
    const icon = role == "contact" ? "silhouette" : "headset";

    return (
        <Column>
            <Row alignItems="center" gap={1}>
                <RVIcon icon={icon} size={RVIconSizeOptions.SMALL} />
                <span className="ant-tag-ellipsis">
                    {sentimentLabel} {capitalize(role)}
                </span>
            </Row>
        </Column>
    );
}
